import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertTriangle, Loader2, LogIn } from 'lucide-react';
import axios from 'axios';
import { errorMessage, takeSignOutReason } from '../api/client';
import { useAuth } from '../store/auth';
import { MaskedInput } from '../components/MaskedInput';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuth((s) => s.login);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * Why the last session ended, if it ended for a reason worth stating.
   *
   * Read once, on mount, and cleared as it is read — so it shows on the
   * redirect that caused it and does not greet somebody who comes back to
   * /login an hour later. useState's initialiser rather than an effect,
   * because an effect would run twice under StrictMode and the second read
   * would find it already taken.
   */
  const [signedOutReason] = useState(takeSignOutReason);
  /**
   * The open shift blocking this login, if the server named one.
   *
   * Holding it is what turns the refusal into something actionable: a plain
   * error would leave somebody whose browser crashed mid-shift with no way
   * back in short of waiting for a nightly job.
   */
  const [shiftConflict, setShiftConflict] = useState<string | null>(null);

  async function attempt(continueShift: boolean) {
    setError(null);
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password, continueShift);
      const from = (location.state as { from?: string } | null)?.from ?? '/';
      navigate(from, { replace: true });
    } catch (err) {
      // 409 is the open-shift refusal, and the only error offering a way
      // forward. Everything else is a plain failure.
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setShiftConflict(errorMessage(err, 'This account is on shift elsewhere'));
      } else {
        setShiftConflict(null);
        setError(errorMessage(err, 'Could not sign in'));
      }
    } finally {
      setBusy(false);
    }
  }

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    void attempt(false);
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-gray-50 px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">
            BNB India
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Staffing, attendance and payroll
          </p>
        </div>

        {/* Why the last session ended. Above the form, because it explains
            why this screen is here at all rather than commenting on the
            attempt somebody has not made yet. */}
        {signedOutReason && (
          <div
            className="mb-4 flex gap-2 rounded-lg bg-amber-50 px-3 py-2.5 text-sm text-amber-900 ring-1 ring-amber-200"
            role="status"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>{signedOutReason}</p>
          </div>
        )}

        <form onSubmit={onSubmit} className="card space-y-4">
          <div>
            <label className="label" htmlFor="email">
              Email
            </label>
            <input
              spellCheck={false}
              id="email"
              type="email"
              autoComplete="username"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div>
            <label className="label" htmlFor="password">
              Password
            </label>
            <MaskedInput
              id="password"
              autoComplete="current-password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {error && (
            <div
              className="space-y-1.5 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700"
              role="alert"
            >
              <p>{error}</p>
              {/* What to do about it.
                  Passwords are issued by an administrator and there is no
                  email in this system, so a failed sign-in has no self-service
                  way forward — and a screen that only says "Incorrect email or
                  password" leaves people guessing at a password that may have
                  been replaced without them being told. Naming who can fix it,
                  and where, is the difference between a message and a way out.
                  Covers a disabled account too: same people, same screen. */}
              <p className="text-xs text-red-600">
                Forgotten it, or had it reset? Your administrator or manager can
                issue a new one from <strong>Team → Users</strong>. If you were
                given a password recently, use that exact one — it replaces
                anything older.
              </p>
            </div>
          )}

          {/* The open-shift refusal, with the way past it.
              Separate from `error` because it is not a dead end: the shift is
              the thing in the way, and carrying it over is a decision this
              person is entitled to make about their own attendance. Without
              this a browser that died mid-shift locks its owner out until a
              nightly job closes the session. */}
          {shiftConflict && (
            <div
              className="space-y-2 rounded-lg bg-amber-50 px-3 py-2.5 text-sm text-amber-900 ring-1 ring-amber-200"
              role="alert"
            >
              <div className="flex gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p>{shiftConflict}</p>
              </div>
              <button
                type="button"
                className="btn-ghost w-full text-sm"
                disabled={busy}
                onClick={() => void attempt(true)}
              >
                Continue that shift here
              </button>
              <p className="text-xs text-amber-700">
                Your shift keeps running, with the same start time. Check-ins
                carry on from this machine.
              </p>
            </div>
          )}

          <button type="submit" className="btn-primary w-full" disabled={busy}>
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <LogIn className="h-4 w-4" />
            )}
            {busy ? 'Signing in' : 'Sign in'}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-gray-400">
          Accounts are created by your administrator.
        </p>
      </div>
    </div>
  );
}
